package org.vreo.devicehub.features.vrmOutput.presentation

import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.presentation.UiText
import org.vreo.devicehub.features.core.presentation.stingInputToFloatFormatter.validateAndFormatAmount
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputConfig
import org.vreo.devicehub.features.core.domain.device.vrmConfig.VrmOutputType
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.domain.settingsRepository.SettingsRepository
import org.vreo.devicehub.features.vrmOutput.domain.VrmOutputRepository
import org.vreo.devicehub.features.vrmOutput.domain.models.VrmOverlayerPosition
import org.vreo.devicehub.features.vrmOutput.presentation.components.getTimeZonesFromJson

/**
 * VRM output view model, responsible for managing the VRM (Vehicle Registration Mark) output
 * configuration(s) of the selected device.
 *
 * The screen edits one [VrmOutputConfig] at a time: [VrmOutputState.selectedVrmId] tracks which
 * entry is currently loaded into the editable form fields on [VrmOutputState]. Switching the
 * selection or saving first flushes those fields back into
 * [VrmOutputState.vrmConfigurations] via [persistSelectedVrmConfig].
 *
 * @property authentication user and target device authentication
 * @property vrmRepository repository for accessing device APIs
 * @property maxVrms maximum number of allowed VRM configurations
 */
class VrmOutputViewModel(
    private val authentication: DeviceAuthentication,
    private val vrmRepository: VrmOutputRepository,
    private val settingsRepository: SettingsRepository,
    private val maxVrms: Int = 5,
) : ViewModel() {

    /** Lower/upper bounds, in percent, accepted by [VrmOutputState.vrmConfidenceThreshold]. */
    val miniConfidenceScoreLimit: Float = 0f
    val maxConfidenceScoreLimit: Float = 95f

    /** Lower/upper bounds, in seconds, accepted by [VrmOutputState.vrmTimeoutSeconds]. */
    val miniTimeoutSeconds: Int = 1
    val maxTimeoutSeconds: Int = 30

    /*** Available/supported ROI instance names/identifers. */
    val availableRoi: List<Int> = listOf(1, 2, 3)

    /** Pool of IDs handed out to new VRM entries; wider than [maxVrms] so freed IDs can be reused. */
    private val availableVrmIds = 1..10

    private val _state = MutableStateFlow(VrmOutputState(isDeviceActive = authentication.isDeviceActive))

    /** UI state for the VRM output screen. */
    val state = _state
        .onStart { loadInitialData() }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000L),
            initialValue = _state.value
        )

    /**
     * Handles a single [VrmOutputActions] emitted by the UI.
     *
     * @param action action to be handled
     */
    fun onAction(action: VrmOutputActions) {
        when (action) {
            is VrmOutputActions.OnClearError -> _state.update { it.copy(error = null) }
            is VrmOutputActions.OnLoadCurrentVrmConfigClicked -> getVrmConfiguration()
            is VrmOutputActions.OnSaveClicked -> saveVrmConfig()

            // VRM list management
            is VrmOutputActions.OnUpdateSelectedVrm -> updateSelectedVrmConfiguration(action.id)
            is VrmOutputActions.OnDeleteVrm -> deleteVrm(action.id)
            is VrmOutputActions.OnAddVrm -> addVrm()

            // Main configuration
            is VrmOutputActions.OnUpdateReportingToDeviceHub -> _state.update { it.copy(deviceHubReportingEnabled = action.value) }
            is VrmOutputActions.OnUpdateVrmEnable -> _state.update { it.copy(vrmEnabled = action.value) }
            is VrmOutputActions.OnUpdateVrmEndpoint -> _state.update { it.copy(vrmEndpoint = action.value) }
            is VrmOutputActions.OnUpdateVrmType -> _state.update { it.copy(vrmTypeIndex = action.index) }
            is VrmOutputActions.OnUpdateVrmApiKey -> _state.update { it.copy(vrmApiKey = action.value) }
            is VrmOutputActions.OnUpdateVrmSiteId -> _state.update { it.copy(vrmSiteId = action.value) }
            is VrmOutputActions.OnUpdateBayIdMode -> _state.update { it.copy(useRoiIdAsBayId = action.value) }
            is VrmOutputActions.OnUpdateRoiBayIdIndex -> _state.update { it.copy(roiIdIndex = action.value) }
            is VrmOutputActions.OnUpdateVrmBayIdentifier -> _state.update { it.copy(vrmBayIdentifier = action.value) }
            is VrmOutputActions.OnUpdateVrmConfidenceThreshold -> updateConfidenceThreshold(action.value)
            is VrmOutputActions.OnUpdateVrmTimeoutSeconds -> _state.update { it.copy(vrmTimeoutSeconds = action.value) }
            is VrmOutputActions.OnUpdateVrmVerifySsl -> _state.update { it.copy(vrmVerifySsl = action.value) }

            // Overlayer
            is VrmOutputActions.OnUpdateOverlayerEnable -> _state.update { it.copy(enableOverlayer = action.value) }
            is VrmOutputActions.OnUpdateOverlayerPosition -> _state.update { it.copy(overlayerPositionIndex = action.index) }
            is VrmOutputActions.OnEnableOverlayerLabel -> _state.update { it.copy(enableOverlayerLabel = action.value) }
            is VrmOutputActions.OnUpdateOverlayerLabel -> _state.update { it.copy(overlayerLabel = action.value) }
            is VrmOutputActions.OnEnableOverlayerOcrResults -> _state.update { it.copy(enableOverlayerOcrResults = action.value) }
            is VrmOutputActions.OnEnableOverlayerBayId -> _state.update { it.copy(enableOverlayerBayId = action.value) }
            is VrmOutputActions.OnEnableOverlayerSiteId -> _state.update { it.copy(enableOverlayerSiteId = action.value) }
            is VrmOutputActions.OnEnableOverlayerDateTime -> _state.update { it.copy(enableOverlayerDateTime = action.value) }
            is VrmOutputActions.OnUpdateTimeZoneIndex -> _state.update { it.copy(selectedTimeZoneIndex = action.value) }
        }
    }

    /**
     * Loads the available timezones and, if the device is reachable, its current VRM
     * configuration. Runs once when [state] first gains a subscriber.
     */
    private suspend fun loadInitialData() {
        val timeZones = try {
            getTimeZonesFromJson("files/staticTimeZones.json")
        } catch (_: Exception) {
            emptyList()
        }
        _state.update { it.copy(availableTimeZones = timeZones) }

        if (authentication.isDeviceActive) {
            getVrmConfiguration()
            viewModelScope.launch { getDeviceConfig() }
        }
    }

    /**
     * Load device configuration/settins. Mainly used to get reporting to device hub state.
     */
    private suspend fun getDeviceConfig() {
        // Get settings
        runCatching {
            // Get device settings
            val deviceSettingsResult = settingsRepository.getDeviceSettings(
                token = authentication.userToken,
                deviceId = authentication.deviceId
            )

            deviceSettingsResult
                .onSuccess { data ->
                    _state.update { it.copy(deviceHubReportingEnabled = data.enableDeviceHubReporting) }
                }
                .onError { _ ->
                    _state.update {
                        it.copy(
                            deviceHubReportingEnabled = false,
                            error = DataError.Remote.INVALID_ARGUMENT.toUiText()
                        )
                    }
                }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Set device configuration, sets reporting to device hub field
     */
    private suspend fun setDeviceConfig() {
        runCatching {
            val currentState = _state.value

            // Get latest config, prevents override of user settings wit default values
            val getResults = settingsRepository.getDeviceSettings(
                token = authentication.userToken,
                deviceId = authentication.deviceId
            )

            val config = when (getResults) {
                is Result.Success -> {
                    getResults.data
                }

                is Result.Error -> {
                    _state.update { it.copy(error = getResults.error.toUiText()) }
                    return@runCatching
                }
            }

            // Send new configuration
            settingsRepository.setDeviceSettings(
                token = authentication.userToken,
                deviceId = authentication.deviceId,
                config = config.copy(enableDeviceHubReporting = currentState.deviceHubReportingEnabled)
            ).onError { error ->
                _state.update { it.copy(error = error.toUiText()) }
            }

        }.getOrElse {
            _state.update { it.copy(error = DataError.Local.UNKNOWN.toUiText()) }
        }
    }

    /**
     * Selects [id] as the active VRM entry, first persisting whichever entry was previously
     * being edited.
     *
     * @param id target VRM identifier; if it no longer exists the selection is cleared with an error
     */
    private fun updateSelectedVrmConfiguration(id: Int) = viewModelScope.launch {
        if (!persistSelectedVrmConfig()) return@launch

        val currentState = _state.value
        if (currentState.vrmConfigurations.isEmpty()) {
            _state.update { it.copy(selectedVrmId = null) }
            return@launch
        }

        val selectedVrm = currentState.vrmConfigurations.find { it.id == id }
        if (selectedVrm == null) {
            _state.update { it.copy(selectedVrmId = null, error = DataError.Local.INVALID_ARGUMENT.toUiText()) }
            return@launch
        }

        setAllFields(selectedVrm)
        _state.update {
            it.copy(
                selectedVrmId = id,
                invalidVrmEndpoint = false,
                invalidConfidenceScoreLimit = false,
                invalidTimeoutSeconds = false,
            )
        }
    }

    /**
     * Removes the VRM entry identified by [id], clearing the selection if it was the entry
     * being edited.
     *
     * @param id VRM identifier to remove
     */
    private fun deleteVrm(id: Int) {
        val currentState = _state.value
        if (currentState.vrmConfigurations.isEmpty()) return

        if (currentState.selectedVrmId == id) {
            _state.update { it.copy(selectedVrmId = null) }
        }

        _state.update {
            it.copy(vrmConfigurations = it.vrmConfigurations.filterNot { vrm -> vrm.id == id })
        }
    }

    /**
     * Adds a new, default VRM entry using the smallest free id in [availableVrmIds], unless
     * [maxVrms] has already been reached.
     */
    private fun addVrm() {
        val currentState = _state.value
        if (currentState.vrmConfigurations.size >= maxVrms) {
            setError(DataError.Local.INVALID_STATE.toUiText())
            return
        }

        val existingIds = currentState.vrmConfigurations.map { it.id }.toSet()
        val nextId = availableVrmIds.firstOrNull { it !in existingIds } ?: return
        val newVrm = VrmOutputConfig(id = nextId, enabled = true, vrmType = VrmOutputType.ZATPARK, useRoiId = false)

        _state.update {
            it.copy(vrmConfigurations = (it.vrmConfigurations + newVrm).sortedBy { vrm -> vrm.id })
        }
    }

    /**
     * Formats and clamps [value] into [VrmOutputState.vrmConfidenceThreshold].
     *
     * @param value raw user input for the confidence threshold
     */
    private fun updateConfidenceThreshold(value: String) {
        try {
            _state.update { it.copy(vrmConfidenceThreshold = validateAndFormatAmount(value = value)) }
        } catch (_: IllegalArgumentException) {
            setError(DataError.Local.INVALID_ARGUMENT.toUiText())
        } catch (_: Exception) {
            setError(DataError.Local.UNKNOWN.toUiText())
        }
    }

    /**
     * Fetches the VRM configuration list from the selected device, replacing
     * [VrmOutputState.vrmConfigurations] on success.
     */
    private fun getVrmConfiguration() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        if (!_state.value.isDeviceActive) {
            setError(DataError.Remote.SERVICE_UNAVAILABLE.toUiText())
            return@launch
        }

        runRepositoryCall(
            call = {
                vrmRepository.getConfiguration(
                    token = authentication.userToken,
                    deviceId = authentication.deviceId
                )
            },
            onSuccess = { vrms ->
                if (vrms.isNotEmpty()) {
                    _state.update {
                        it.copy(
                            selectedVrmId = null,
                            invalidVrmEndpoint = false,
                            invalidConfidenceScoreLimit = false,
                            invalidTimeoutSeconds = false,
                            vrmConfigurations = vrms,
                        )
                    }
                }
            }
        )
    }

    /**
     * Persists the currently edited entry, validates every VRM configuration, and sends the
     * full list to the device.
     */
    private fun saveVrmConfig() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        if (!persistSelectedVrmConfig()) return@launch

        val currentState = _state.value
        if (!currentState.isDeviceActive) {
            setError(DataError.Remote.SERVICE_UNAVAILABLE.toUiText())
            return@launch
        }

        if (currentState.vrmConfigurations.any { !it.isValid() }) {
            setError(DataError.Local.INVALID_ARGUMENT.toUiText())
            return@launch
        }

        // Update device config
        setDeviceConfig()

        // Update VRM output config
        runRepositoryCall(
            call = {
                vrmRepository.setConfiguration(
                    token = authentication.userToken,
                    deviceId = authentication.deviceId,
                    config = currentState.vrmConfigurations
                )
            },
            onSuccess = {}
        )
    }

    /**
     * Validates and flushes the editable form fields on [VrmOutputState] back into the currently
     * selected entry in [VrmOutputState.vrmConfigurations].
     *
     * @return `true` if nothing was selected, or the fields were valid and stored; `false` if
     * validation failed, in which case [VrmOutputState.error] has already been set
     */
    private fun persistSelectedVrmConfig(): Boolean {
        val selectedId = _state.value.selectedVrmId ?: return true

        val validated = validateVrmConfigInputState(_state.value)
        _state.update { validated }

        if (validated.invalidVrmEndpoint || validated.invalidConfidenceScoreLimit || validated.invalidTimeoutSeconds) {
            setError(DataError.Local.INVALID_ARGUMENT.toUiText())
            return false
        }

        storeCurrentVrmConfigurationItem(validated, selectedId)
        return true
    }

    /**
     * Populates the editable form fields on [VrmOutputState] from [config], e.g. when a VRM
     * entry is selected.
     *
     * @param config configuration to load into the editable fields
     */
    private fun setAllFields(config: VrmOutputConfig) {
        val timeZoneIndex = config.timeZone
            ?.let { zone -> _state.value.availableTimeZones.indexOfFirst { it.id.equals(zone, ignoreCase = true) } }
            ?.coerceAtLeast(0)
            ?: 0

        val bayIdIndex = if (config.useRoiId) {
            val receivedBayId = config.bayIdentifier.toIntOrNull() ?: 0
            availableRoi.indexOfFirst { it == receivedBayId }
        } else {
            0
        }

        println("VRM TIMEZONE: ${config.timeZone}")

        _state.update {
            it.copy(
                // General VRM config
                vrmEnabled = config.enabled,
                vrmTypeIndex = VrmOutputType.entries.indexOf(config.vrmType).coerceAtLeast(0),
                vrmEndpoint = config.endpoint,
                vrmApiKey = config.apiKey,
                vrmSiteId = config.siteId,
                useRoiIdAsBayId = config.useRoiId,
                roiIdIndex = bayIdIndex,
                vrmBayIdentifier = config.bayIdentifier,
                vrmConfidenceThreshold = config.confidenceThreshold.toString(),
                vrmTimeoutSeconds = config.timeoutSeconds.toString(),
                vrmVerifySsl = config.verifySsl,

                // Overlayer
                enableOverlayer = config.enableOverlayer,
                overlayerPositionIndex = VrmOverlayerPosition.entries
                    .indexOf(config.overlayerPosition)
                    .coerceAtLeast(0),
                enableOverlayerLabel = !config.overlayerLabel.isNullOrEmpty(),
                overlayerLabel = config.overlayerLabel ?: "",
                enableOverlayerOcrResults = config.enableOverlayerOcrResults,
                enableOverlayerBayId = config.enableOverlayerBayId,
                enableOverlayerSiteId = config.enableOverlayerSiteId,
                enableOverlayerDateTime = !config.timeZone.isNullOrBlank(),
                selectedTimeZoneIndex = timeZoneIndex
            )
        }
    }

    /**
     * Builds a [VrmOutputConfig] from the editable form fields on [currentState] and writes it
     * back into [VrmOutputState.vrmConfigurations] at [id]. Overlayer sub-fields (label, OCR
     * results, bay/site id, timezone) are only kept when the overlayer master switch is enabled.
     *
     * @param currentState state holding the edited form fields
     * @param id identifier of the VRM entry being updated
     */
    private fun storeCurrentVrmConfigurationItem(currentState: VrmOutputState, id: Int) {
        if (currentState.vrmConfigurations.none { it.id == id }) {
            setError(DataError.Local.INVALID_ARGUMENT.toUiText())
            return
        }

        val overlayerLabel = currentState.overlayerLabel
            .takeIf { currentState.enableOverlayer && currentState.enableOverlayerLabel }
            ?.ifBlank { null }
        val timeZone = currentState.availableTimeZones.getOrNull(currentState.selectedTimeZoneIndex)?.id
            .takeIf { currentState.enableOverlayer && currentState.enableOverlayerDateTime }

        val bayId = if (currentState.useRoiIdAsBayId) {
            availableRoi[currentState.roiIdIndex].toString()
        } else {
            currentState.vrmBayIdentifier
        }

        val newConfig = try {
            VrmOutputConfig(
                id = id,
                enabled = currentState.vrmEnabled,
                vrmType = VrmOutputType.entries[currentState.vrmTypeIndex],
                endpoint = currentState.vrmEndpoint,
                apiKey = currentState.vrmApiKey,
                siteId = currentState.vrmSiteId,
                useRoiId = currentState.useRoiIdAsBayId,
                bayIdentifier = bayId,
                confidenceThreshold = currentState.vrmConfidenceThreshold.toFloat(),
                timeoutSeconds = currentState.vrmTimeoutSeconds.toInt(),
                verifySsl = currentState.vrmVerifySsl,
                enableOverlayer = currentState.enableOverlayer,
                overlayerPosition = VrmOverlayerPosition.entries[currentState.overlayerPositionIndex],
                overlayerLabel = overlayerLabel,
                enableOverlayerOcrResults = currentState.enableOverlayer && currentState.enableOverlayerOcrResults,
                enableOverlayerBayId = currentState.enableOverlayer && currentState.enableOverlayerBayId,
                enableOverlayerSiteId = currentState.enableOverlayer && currentState.enableOverlayerSiteId,
                timeZone = timeZone
            )
        } catch (_: Exception) {
            setError(DataError.Local.INVALID_ARGUMENT.toUiText())
            return
        }

        _state.update {
            it.copy(vrmConfigurations = it.vrmConfigurations.map { vrm -> if (vrm.id == id) newConfig else vrm })
        }
    }

    /**
     * Clamps the numeric fields on [currentState] into their allowed ranges and recomputes the
     * `invalid*` flags used to block saving/switching selection.
     *
     * @param currentState state to validate
     * @return a copy of [currentState] with clamped values and refreshed `invalid*` flags
     */
    private fun validateVrmConfigInputState(currentState: VrmOutputState): VrmOutputState {
        val clampedConfidence = (currentState.vrmConfidenceThreshold.toFloatOrNull() ?: 0f)
            .coerceIn(miniConfidenceScoreLimit, maxConfidenceScoreLimit)
        val clampedTimeoutSeconds = (currentState.vrmTimeoutSeconds.toIntOrNull() ?: 0)
            .coerceIn(miniTimeoutSeconds, maxTimeoutSeconds)

        return currentState.copy(
            vrmConfidenceThreshold = clampedConfidence.toString(),
            vrmTimeoutSeconds = clampedTimeoutSeconds.toString(),
            vrmApiKey = currentState.vrmApiKey.ifBlank { "None" },
            vrmSiteId = currentState.vrmSiteId.ifBlank { "None" },
            vrmBayIdentifier = currentState.vrmBayIdentifier.ifBlank { "None" },
            invalidVrmEndpoint = isEndpointInvalid(currentState.vrmEndpoint),
            invalidConfidenceScoreLimit = isConfidenceInvalid(clampedConfidence),
            invalidTimeoutSeconds = isTimeoutInvalid(clampedTimeoutSeconds),
        )
    }

    /**
     * Validates a persisted VRM entry before it is sent to the device.
     *
     * @receiver entry to validate
     * @return `true` if the endpoint, confidence threshold and timeout are all within range
     */
    private fun VrmOutputConfig.isValid(): Boolean =
        !isEndpointInvalid(endpoint) && !isConfidenceInvalid(confidenceThreshold) && !isTimeoutInvalid(timeoutSeconds)

    /** @return `true` if [endpoint] is blank or missing a `http(s)://` scheme */
    private fun isEndpointInvalid(endpoint: String) =
        endpoint.isBlank() || !(endpoint.startsWith("http://") || endpoint.startsWith("https://"))

    /** @return `true` if [confidence] falls outside [miniConfidenceScoreLimit]..[maxConfidenceScoreLimit] */
    private fun isConfidenceInvalid(confidence: Float) =
        confidence !in miniConfidenceScoreLimit..maxConfidenceScoreLimit

    /** @return `true` if [timeoutSeconds] falls outside [miniTimeoutSeconds]..[maxTimeoutSeconds] */
    private fun isTimeoutInvalid(timeoutSeconds: Int) =
        timeoutSeconds !in miniTimeoutSeconds..maxTimeoutSeconds

    /**
     * Surfaces [error] to the UI and clears [VrmOutputState.isLoading].
     *
     * @param error error to display
     */
    private fun setError(error: UiText) {
        _state.update { it.copy(isLoading = false, error = error) }
    }

    /**
     * Executes a repository [call], forwarding a successful result to [onSuccess] or mapping a
     * failure into [VrmOutputState.error]. [VrmOutputState.isLoading] is always cleared once the
     * call settles.
     *
     * @param call suspend function invoking the repository
     * @param onSuccess invoked with the success payload, if any
     */
    private suspend fun <T> runRepositoryCall(
        call: suspend () -> Result<T, DataError.Remote>,
        onSuccess: (T) -> Unit,
    ) {
        when (val result = runCatching { call() }.getOrNull()) {
            is Result.Success -> onSuccess(result.data)
            is Result.Error -> _state.update { it.copy(error = result.error.toUiText()) }
            null -> _state.update { it.copy(error = DataError.Local.UNKNOWN.toUiText()) }
        }
        _state.update { it.copy(isLoading = false) }
    }
}
